// Asks before leaving an editor with unsaved changes (plan R6 AB-1): a reload,
// a closed tab or an external link (`beforeunload`), and an in-app navigation
// (sidebar, Back button). react-router 6.3 has no useBlocker, so the in-app
// part blocks through the router's history navigator (history 5 `block`);
// without it only `beforeunload` remains.
import { useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

type Transition = { retry: () => void };
type BlockingNavigator = { block?: (blocker: (tx: Transition) => void) => () => void };

export default function useLeaveGuard(when: boolean, message: string) {
  const { navigator } = useContext(UNSAFE_NavigationContext);

  useEffect(() => {
    if (!when) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [when, message]);

  useEffect(() => {
    const { block } = navigator as unknown as BlockingNavigator;
    if (!when || typeof block !== 'function') return undefined;
    const unblock = block.call(navigator, (tx: Transition) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(message)) return;
      unblock();
      tx.retry();
    });
    return unblock;
  }, [navigator, when, message]);
}
